// Blog filtres N2 + pagination — sans rechargement (Section Rendering API + morph).
//
// Les pills N2 sont des liens natifs (/blogs/{blog}/tagged/...). Au clic on intercepte,
// on met la liste en état de chargement, on met à jour l'URL (pushState) puis on re-rend
// EN PLACE les sections qui changent :
//   - `ax-blog-list`            → la grille d'articles filtrée,
//   - `ax-blog-filters` level 2 → les états actifs des pills (+ « Tout voir »),
//   - `ax-blog-filters` level 1 → ses liens, qui cumulent la sélection N2 (les deux barres
//                                 sont indépendantes mais leurs filtres s'additionnent).
// Pas de reload → aucun saut de scroll, aucun flash. Les clics sur la barre N1 (qui pilote
// aussi la section « à la une ») gardent leur comportement natif : non interceptés ici.
//
// Fallback : sans les sections attendues, ou en cas d'erreur réseau, on laisse la
// navigation native se faire (les pills restent de simples liens).
import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';

const listWrapper = document.querySelector('.ax-blog-list')?.closest('.shopify-section');
const n2Wrapper = document.querySelector('.ax-blog-filters--level-2')?.closest('.shopify-section');
const n1Wrapper = document.querySelector('.ax-blog-filters--level-1')?.closest('.shopify-section');

if (listWrapper && n2Wrapper) {
  const listId = normalizeSectionId(listWrapper.id);
  const n2Id = normalizeSectionId(n2Wrapper.id);
  const n1Id = n1Wrapper ? normalizeSectionId(n1Wrapper.id) : null;

  const setLoading = (isLoading) => {
    // `.ax-blog-list` est reconstruit par le morph → la classe disparaît d'elle-même une
    // fois la nouvelle liste en place. On re-query à chaque fois (le nœud peut changer).
    const list = document.querySelector('.ax-blog-list');
    if (!list) return;
    list.classList.toggle('ax-blog-list--loading', isLoading);
    if (isLoading) {
      list.setAttribute('aria-busy', 'true');
    } else {
      list.removeAttribute('aria-busy');
    }
  };

  /**
   * Re-rend la liste + les barres de filtres pour l'URL courante.
   * @returns {Promise<void>}
   */
  const render = async () => {
    setLoading(true);
    try {
      // La barre 1 n'est PAS re-rendue : elle ne filtre plus la liste, et ses `href` ne
      // servent que de repli sans JS — où la navigation native rafraîchit tout de toute
      // façon. La re-rendre écraserait en prime la mise en avant sélectionnée.
      await Promise.all([
        sectionRenderer.renderSection(listId),
        sectionRenderer.renderSection(n2Id),
      ]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ramène la vue sur le titre de la liste. `scroll-margin-block-start` est déjà
   * posé sur la barre N2 pour compenser le header sticky.
   * @returns {void}
   */
  const revenirEnHautDeListe = () => {
    const cible = document.querySelector('.ax-blog-filters--level-2') || document.querySelector('.ax-blog-list');
    if (!cible) return;

    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cible.scrollIntoView({ behavior: reduit ? 'auto' : 'smooth', block: 'start' });
  };

  // Le choix de la barre 1 vit dans un PARAMÈTRE de requête, pas dans le chemin : un
  // segment `/tagged/` déclencherait le filtrage natif de Shopify sur la liste aussi, et
  // recréerait le couplage entre les deux barres. Conséquence assumée : Liquid ne sait pas
  // lire un paramètre, la zone « à la une » est donc rendue non filtrée par le serveur puis
  // remplacée au chargement — d'où l'état de chargement pendant l'échange.
  const PARAM_UNE = 'une';

  const barreUne = () => document.querySelector('[data-featured-filter]');
  const idFeatured = () =>
    normalizeSectionId(document.querySelector('.ax-blog-featured')?.closest('[id^="shopify-section-"]')?.id ?? '');

  /**
   * Re-rend la zone « à la une » à l'URL du tag, où le filtrage natif s'applique.
   * @param {string} tag - Tag handleizé, chaîne vide pour la sélection complète.
   * @returns {Promise<void>}
   */
  const rendreUne = async (tag) => {
    const barre = barreUne();
    const id = idFeatured();
    if (!barre || !id) return;

    const zone = document.querySelector('.ax-blog-featured');
    zone?.classList.add('ax-blog-featured--loading');
    zone?.setAttribute('aria-busy', 'true');

    const base = barre.dataset.blogUrl || window.location.pathname;
    try {
      await sectionRenderer.renderSection(id, {
        url: new URL(tag ? `${base}/tagged/${tag}` : base, window.location.origin),
      });
    } finally {
      // Le morph remplace le nœud : on le re-cherche plutôt que de réutiliser `zone`.
      const apres = document.querySelector('.ax-blog-featured');
      apres?.classList.remove('ax-blog-featured--loading');
      apres?.removeAttribute('aria-busy');
    }
  };

  /**
   * Aligne l'état actif des pastilles de la barre 1 sur le tag courant.
   * @param {string} tag - Tag handleizé, chaîne vide pour aucun filtre.
   * @returns {void}
   */
  const syncPastillesUne = (tag) => {
    const barre = barreUne();
    if (!barre) return;

    for (const p of barre.querySelectorAll('.ax-blog-filters__pill')) {
      const actif = tag !== '' && p.dataset.featuredTag === tag;
      p.classList.toggle('ax-blog-filters__pill--active', actif);
      if (actif) {
        p.setAttribute('aria-current', 'true');
      } else {
        p.removeAttribute('aria-current');
      }
    }
  };

  /** @returns {string} Le tag de mise en avant porté par l'URL courante. */
  const tagUneCourant = () => new URL(window.location.href).searchParams.get(PARAM_UNE) ?? '';

  document.addEventListener('click', (event) => {
    // Laisser passer les clics « ouvrir dans un nouvel onglet » / non-primaires.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const pill = event.target.closest('a.ax-blog-filters__pill');

    // Barre 1 — sélecteur de mise en avant : ne touche ni l'URL ni la liste, il re-rend
    // la seule zone « à la une » à l'URL du tag, où Shopify applique son filtrage natif.
    const pillUne = pill?.hasAttribute('data-featured-tag') ? pill : null;
    if (pillUne) {
      event.preventDefault();

      // Cette barre n'a pas de « Tout voir » : recliquer la pastille active la
      // désélectionne et ramène la mise en avant complète. Sans ça, il n'y aurait
      // aucun retour en arrière une fois un thème choisi.
      const deselection = pillUne.classList.contains('ax-blog-filters__pill--active');

      if (!barreUne() || !idFeatured()) {
        window.location.href = pillUne.href;
        return;
      }

      const tag = deselection ? '' : pillUne.dataset.featuredTag;
      syncPastillesUne(tag);

      // On inscrit le choix dans l'URL : le lien devient partageable, et un rechargement
      // ou un retour arrière retrouve la même mise en avant.
      const adresse = new URL(window.location.href);
      if (tag) {
        adresse.searchParams.set(PARAM_UNE, tag);
      } else {
        adresse.searchParams.delete(PARAM_UNE);
      }
      history.pushState({ axBlogFilter: true }, '', adresse);

      rendreUne(tag).catch(() => {
        window.location.href = pillUne.href; // repli : navigation native
      });
      return;
    }

    const estFiltre = pill?.closest('.ax-blog-filters--level-2') && pill.href;

    // Pagination : mêmes sections à re-rendre, mais on ramène la vue sur le titre
    // « Nos derniers articles » — sans quoi le lecteur resterait devant la pagination,
    // c'est-à-dire en bas de la nouvelle page.
    const page = event.target.closest('a.ax-pagination__page');
    const estPagination = page?.closest('.ax-blog-list') && page.href;

    const lien = estFiltre ? pill : estPagination ? page : null;
    if (!lien) return;

    event.preventDefault();

    const url = new URL(lien.href);

    // Le choix de mise en avant survit à une navigation dans la liste : sans ce report,
    // filtrer la liste effacerait la sélection du haut, que les deux barres soient
    // pourtant indépendantes.
    const une = tagUneCourant();
    if (une) url.searchParams.set(PARAM_UNE, une);

    const dejaLa = url.pathname === window.location.pathname && url.search === window.location.search;
    if (dejaLa) return;

    history.pushState({ axBlogFilter: true }, '', url);
    render()
      .then(() => {
        if (estPagination) revenirEnHautDeListe();
      })
      .catch(() => {
        window.location.href = lien.href; // fallback : navigation native
      });
  });

  // Précédent / suivant : re-rendre pour l'URL restaurée (sans repush), les deux zones.
  window.addEventListener('popstate', () => {
    const tag = tagUneCourant();
    syncPastillesUne(tag);
    rendreUne(tag).catch(() => window.location.reload());
    render().catch(() => window.location.reload());
  });

  // Arrivée sur une adresse portant déjà un choix de mise en avant (lien partagé,
  // rechargement, résultat de recherche) : le serveur a rendu la sélection complète,
  // on la remplace par celle demandée.
  const tagInitial = tagUneCourant();
  if (tagInitial) {
    syncPastillesUne(tagInitial);
    rendreUne(tagInitial).catch(() => {
      /* la sélection complète reste affichée : dégradation acceptable */
    });
  }
}
